/**
 * MIDI Learn controller (MDL-315).
 *
 * Bridges a device connection's arm/disarm commands and its `midiLearnEnded`
 * push event into a promise-based API the CC assignment modal can await. One
 * learn session at a time (the spec's model).
 *
 * `start()` arms the device and resolves when the device reports the outcome
 * (`{ reason: 'captured', cc, channel }` / `'cancelled'` / `'timeout'`).
 * `cancel()` resolves the pending promise immediately and disarms the device,
 * so a disconnect mid-learn can never leave the UI hanging.
 *
 * `connection` must implement `startMidiLearn()`, `cancelMidiLearn()`, and
 * `onMidiLearnEnded(cb)` (all three connection classes do).
 */
export function createMidiLearnController(connection) {
  let pending = null;

  const settle = (msg) => {
    if (pending) {
      const resolve = pending;
      pending = null;
      resolve(msg);
    }
  };

  if (connection && typeof connection.onMidiLearnEnded === 'function') {
    connection.onMidiLearnEnded(settle);
  }

  return {
    // Arm capture. Resolves with the device's midiLearnEnded payload.
    async start() {
      settle({ reason: 'cancelled' }); // abandon any stale session first
      await connection.startMidiLearn();
      return new Promise((resolve) => { pending = resolve; });
    },

    // Disarm. Resolves the UI immediately; the device's own cancelled echo
    // that follows is a no-op (pending already cleared).
    cancel() {
      settle({ reason: 'cancelled' });
      try { connection.cancelMidiLearn?.(); } catch { /* connection gone */ }
    },
  };
}
